import React, { useState, useEffect } from 'react';
import { MdOutlineEmail, MdLockOutline, MdCheckCircle, MdInfoOutline, MdArrowBack, MdArrowForward } from 'react-icons/md';
import { useFormContext } from '../../context/FormContext';
import { useReadOnly } from '../../hooks/useReadOnly';
import { SignupLayout } from '../ui/SignupLayout';
import { updateQuote } from '../services/QuoteService';
import { updatePassword } from '../services/AccountService';
import { formatDate, formatCurrency } from '../util/formatters';
import Session from '../auth/Session';

// -- Helpers
const TRIGGER_DISPLAY_NAMES = {
    single: 'Proxy Wind',
    dual: 'Proxy Wind + Anemometer',
    triple: 'Proxy Wind + Anemometer + Storm Track',
};

export function FinalizePolicy({ history }) {
    const { formData, setField, setStep } = useFormContext();
    const { isReadOnly } = useReadOnly();

    const [password, setPassword] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [emailSent, setEmailSent] = useState(false);
    const [pdfLoading, setPdfLoading] = useState(false);
    const [pdfError, setPdfError] = useState('');

    useEffect(() => {
        setStep('finalize');
    }, []);

    // -- Derived values from context
    const selectedOption = formData.RateOptions?.find(
        (o) => o.trigger_type === formData.SelectedTriggerType
    );

    const propertyAddress = [
        formData.TargetAddressOne,
        formData.TargetAddressTwo,
        formData.TargetCity && formData.TargetState
            ? `${formData.TargetCity}, ${formData.TargetState} ${formData.TargetZipCode}`
            : '',
    ].filter(Boolean).join('\n');

    const triggerDisplayName = TRIGGER_DISPLAY_NAMES[formData.SelectedTriggerType] || formData.SelectedTriggerType || '—';

    // -- View sample policy PDF
    const handleViewSamplePolicy = async () => {
        if (!formData.SelectedTriggerType) return;
        setPdfLoading(true);
        setPdfError('');

        try {
            const token = Session.getToken();
            const response = await fetch(
                `api/SamplePolicy/GetSamplePolicy?triggerType=${formData.SelectedTriggerType}`,
                {
                    method: 'GET',
                    headers: token ? { Authorization: `Bearer ${token}` } : {},
                }
            );

            if (!response.ok) {
                setPdfError('Could not load the sample policy. Please try again.');
                return;
            }

            // Create a blob URL and open it in a new tab
            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            window.open(url, '_blank', 'noopener,noreferrer');

            // Clean up the blob URL after a short delay
            setTimeout(() => URL.revokeObjectURL(url), 10000);
        } catch (err) {
            setPdfError('Could not load the sample policy. Please try again.');
        } finally {
            setPdfLoading(false);
        }
    };

    // -- Send quote to email (stub)
    const handleSendQuoteEmail = () => {
        // TODO: wire to backend endpoint when ready
        setEmailSent(true);
    };

    // -- Password validation
    const validatePassword = () => {
        if (!password.trim()) {
            setPasswordError('Password is required.');
            return false;
        }
        if (password.length < 8) {
            setPasswordError('Password must be at least 8 characters.');
            return false;
        }
        setPasswordError('');
        return true;
    };

    //  Submit -> navigate to DocuSign signing screen
    const handleSignAndSubmit = async () => {
        if (isReadOnly) {
            history.push('/signup/sign');
            return; // skip validation, API calls, everything
        }
        if (!validatePassword()) return;

        // Persist current state to DB
        if (formData.QuoteId) {
            try {
                await updateQuote(formData.QuoteId, formData);
            } catch (err) {
                console.error('Failed to update quote on continue:', err);
            }
        }

        // Update password from temp -> user's chosen password
        try {
            await updatePassword(password);
        } catch (err) {
            console.error('Failed to update password:', err);
        }

        history.push('/signup/sign');
    };

    // -- Back
    const handleBack = () => {
        if (formData.HasAdditionalParties) {
            history.push('/signup/additional-party');
        } else {
            history.push('/signup/confirm-details');
        }
    };

    return (
        <SignupLayout currentStep={2} totalSteps={3} stepLabel="Confirm details">

            <h1 className="signup-heading">Finalize your policy.</h1>
            <p className="signup-section__desc">
                You're almost there - review your coverage and set your portal password.
            </p>

            {/* COVERAGE SUMMARY CARD */}
            <div className="summary-card" style={{ marginBottom: 'var(--space-lg)' }}>
                <p className="summary-card__title">Coverage summary</p>

                {/* Start date */}
                <div className="summary-row">
                    <span className="summary-row__label">Start date</span>
                    <span className="summary-row__value">{formatDate(formData.EffectiveDate)}</span>
                </div>

                {/* Property address */}
                <div className="summary-row">
                    <span className="summary-row__label">Property address</span>
                    <span className="summary-row__value" style={{ whiteSpace: 'pre-line' }}>
                        {propertyAddress || '—'}
                    </span>
                </div>

                {/* Coverage amount + Annual premium side by side */}
                <div className="summary-grid" style={{ marginBottom: 'var(--space-sm)' }}>
                    <div className="summary-row" style={{ marginBottom: 0 }}>
                        <span className="summary-row__label">Coverage amount</span>
                        <span className="summary-row__value">
                            {formatCurrency(formData.CoverageAmount)}
                        </span>
                    </div>
                    <div className="summary-row" style={{ marginBottom: 0 }}>
                        <span className="summary-row__label">Annual premium</span>
                        <span className="summary-row__value">
                            {selectedOption ? 
                            formatCurrency(selectedOption.total_amount_due) 
                            : formData.SelectedTotalPremium
                                ? formatCurrency(formData.SelectedTotalPremium) 
                                : '-'}
                        </span>
                    </div>
                </div>

                {/* Data point option */}
                <div className="summary-row" style={{ marginBottom: 0 }}>
                    <span className="summary-row__label">Data point option</span>
                    <span className="summary-row__value">{triggerDisplayName}</span>
                </div>
            </div>

            {!isReadOnly && (
                <>
                    {/* -- View sample policies link -- */}
                    <div style={{ marginBottom: 'var(--space-lg)' }}>
                        <p className="small">
                            Want a closer look?{' '}
                            <button
                                className="finalize__link-btn"
                                onClick={handleViewSamplePolicy}
                                disabled={pdfLoading || !formData.SelectedTriggerType}
                            >
                                {pdfLoading ? 'Loading…' : 'View sample policies.'}
                            </button>
                        </p>
                        {pdfError && (
                            <span className="error-text">{pdfError}</span>
                        )}
                    </div>

                    {/* -- Send quote to email -- */}
                    {/* {emailSent ? (
                        <div className="finalize__email-sent">
                            <MdCheckCircle size={20} className="finalize__email-sent-icon" />
                            <div>
                                <strong>Quote email sent</strong>
                                <p>
                                    Note your policy has not been submitted yet. Complete the remaining
                                    steps to finalize your coverage.
                                </p>
                            </div>
                        </div>
                    ) : (
                        <button
                            className="btn btn--outline finalize__email-btn"
                            onClick={handleSendQuoteEmail}
                            style={{ width: '100%', marginBottom: 'var(--space-xl)' }}
                        >
                            <MdOutlineEmail size={16} />
                            Send quote to my email
                        </button>
                    )} */}

                    {/* PASSWORD SECTION */}
                    <section className="signup-section" style={{ marginBottom: 'var(--space-md)' }}>
                        <h2 className="signup-section__title">
                            Enter a password for your Elevana Portal.
                        </h2>
                        <p className="signup-section__desc">
                            Once you're issued your policy, you will gain access to your insured portal.
                        </p>

                        <div className="form-group">
                            <p className="signup-field-label"><strong>Please enter a password.</strong></p>
                            <p className="signup-field-hint">Your password must be at least 8 characters.</p>
                            <div className="input-icon-wrap">
                                <span className="input-icon"><MdLockOutline size={16} /></span>
                                <input
                                    type="password"
                                    placeholder="Password"
                                    value={password}
                                    onChange={(e) => {
                                        setPassword(e.target.value);
                                        if (passwordError) setPasswordError('');
                                    }}
                                    style={{ paddingLeft: '36px' }}
                                />
                            </div>
                            {passwordError && (
                                <span className="error-text field-error">{passwordError}</span>
                            )}
                        </div>
                    </section>
                </>
            )}

            {/* -- Navigation -- */}
            <div className="signup-nav">
                <button className="btn signup-cta btn--inverse-cta" onClick={handleBack}>
                    <MdArrowBack /> Back
                </button>
                <button className="btn signup-cta" onClick={handleSignAndSubmit}>
                    Sign & Submit <MdArrowForward />
                </button>
            </div>

            <p className="finalize__disclaimer">
                Submitting will begin your verification. Payment will be collected once your
                information is confirmed.
            </p>
        </SignupLayout>
    );
}